<template>
  <div class="smart-plus-material-report">
    <!-- 页面标题 -->
    <div class="page-header">
      <h2>Smart+ 素材概览报表</h2>
      <div class="header-actions">
        <el-button
            type="primary"
            icon="el-icon-refresh"
            :loading="loading"
            @click="handleQuery"
        >
          刷新数据
        </el-button>
      </div>
    </div>

    <!-- 查询条件 -->
    <el-card shadow="never" class="filter-card">
      <el-form :model="queryForm" :rules="rules" ref="queryForm" inline>
        <el-form-item label="广告主" prop="advertiserId">
          <el-select
              v-model="queryForm.advertiserId"
              placeholder="请选择广告主"
              filterable
              style="width: 240px"
          >
            <el-option
                v-for="item in advertiserList"
                :key="item.advertiser_id"
                :label="`${item.advertiser_name} (${item.advertiser_id})`"
                :value="item.advertiser_id"
            />
          </el-select>
        </el-form-item>

        <el-form-item label="维度分组" prop="dimensions">
          <el-select
              v-model="queryForm.dimensions"
              multiple
              :multiple-limit="2"
              placeholder="请选择2个维度"
              style="width: 280px"
          >
            <el-option
                v-for="item in dimensionOptions"
                :key="item.value"
                :label="item.label"
                :value="item.value"
            />
          </el-select>
        </el-form-item>

        <el-form-item label="查询类型">
          <el-radio-group v-model="queryType">
            <el-radio label="date">日期范围</el-radio>
            <el-radio label="lifetime">生命周期</el-radio>
          </el-radio-group>
        </el-form-item>

        <el-form-item
            v-if="queryType === 'date'"
            label="开始日期"
            prop="startDate"
        >
          <el-date-picker
              v-model="queryForm.startDate"
              type="date"
              placeholder="选择开始日期"
              value-format="yyyy-MM-dd"
              style="width: 160px"
          />
        </el-form-item>

        <el-form-item
            v-if="queryType === 'date'"
            label="结束日期"
            prop="endDate"
        >
          <el-date-picker
              v-model="queryForm.endDate"
              type="date"
              placeholder="选择结束日期"
              value-format="yyyy-MM-dd"
              style="width: 160px"
          />
        </el-form-item>

        <el-form-item label="排序字段">
          <el-select
              v-model="queryForm.sortField"
              placeholder="请选择"
              style="width: 140px"
          >
            <el-option
                v-for="metric in sortFieldOptions"
                :key="metric.value"
                :label="metric.label"
                :value="metric.value"
            />
          </el-select>
        </el-form-item>

        <el-form-item label="排序方式">
          <el-radio-group v-model="queryForm.sortType">
            <el-radio label="DESC">降序</el-radio>
            <el-radio label="ASC">升序</el-radio>
          </el-radio-group>
        </el-form-item>

        <el-form-item>
          <el-button type="primary" @click="handleQuery" :loading="loading">
            查询
          </el-button>
          <el-button @click="resetForm">重置</el-button>
          <el-button
              type="success"
              @click="exportData"
              :disabled="tableData.length === 0"
          >
            导出数据
          </el-button>
        </el-form-item>
      </el-form>
    </el-card>

    <!-- 指标选择 -->
    <el-card shadow="never" class="metrics-card">
      <div slot="header">
        <span>指标选择</span>
        <el-checkbox
            v-model="selectAllMetrics"
            @change="handleSelectAllMetrics"
            style="margin-left: 20px"
        >
          全选
        </el-checkbox>
      </div>
      <el-checkbox-group v-model="selectedMetrics">
        <el-row :gutter="10">
          <el-col :span="6" v-for="metric in metricOptions" :key="metric.value">
            <el-checkbox :label="metric.value">{{ metric.label }}</el-checkbox>
          </el-col>
        </el-row>
      </el-checkbox-group>
    </el-card>

    <!-- 统计信息 -->
    <el-row :gutter="20" class="stats-row">
      <el-col :span="6">
        <el-card shadow="hover" class="stat-card">
          <div class="stat-item">
            <div class="stat-label">素材总数</div>
            <div class="stat-value">{{ pagination.total }}</div>
          </div>
        </el-card>
      </el-col>
      <el-col :span="6">
        <el-card shadow="hover" class="stat-card">
          <div class="stat-item">
            <div class="stat-label">总消耗</div>
            <div class="stat-value">¥{{ formatMoney(totalSpend) }}</div>
          </div>
        </el-card>
      </el-col>
      <el-col :span="6">
        <el-card shadow="hover" class="stat-card">
          <div class="stat-item">
            <div class="stat-label">总展示量</div>
            <div class="stat-value">{{ formatNumber(totalImpressions) }}</div>
          </div>
        </el-card>
      </el-col>
      <el-col :span="6">
        <el-card shadow="hover" class="stat-card">
          <div class="stat-item">
            <div class="stat-label">去重注册次数</div>
            <div class="stat-value">{{ formatNumber(totalRegistrations) }}</div>
          </div>
        </el-card>
      </el-col>
    </el-row>

    <!-- 数据表格 -->
    <el-card shadow="never" class="table-card">
      <el-table
          :data="tableData"
          v-loading="loading"
          stripe
          max-height="600"
          style="width: 100%"
          @selection-change="handleSelectionChange"
      >
        <!-- 添加多选列 -->
        <el-table-column
            type="selection"
            width="55"
            align="center"
            v-if="canShowActionColumn"
        />
        <el-table-column type="index" label="序号" width="60" align="center" />

        <!-- 维度列 -->
        <el-table-column
            v-if="showColumn('campaign_id')"
            prop="dimensions.campaign_id"
            label="推广系列ID"
            min-width="150"
            show-overflow-tooltip
        />

        <el-table-column
            v-if="showColumn('adgroup_id')"
            prop="dimensions.adgroup_id"
            label="广告组ID"
            min-width="150"
            show-overflow-tooltip
        />

        <el-table-column
            v-if="showColumn('smart_plus_ad_id')"
            prop="dimensions.smart_plus_ad_id"
            label="广告ID"
            min-width="150"
            show-overflow-tooltip
        />

        <el-table-column
            prop="dimensions.main_material_id"
            label="素材ID"
            min-width="180"
            show-overflow-tooltip
        />

        <el-table-column
            prop="dimensions.main_material_type"
            label="素材类型"
            width="150"
        >
          <template slot-scope="{ row }">
            <el-tag size="small">
              {{ formatMaterialType(row.dimensions.main_material_type) }}
            </el-tag>
          </template>
        </el-table-column>

        <el-table-column
            label="素材名称"
            min-width="250"
            show-overflow-tooltip
        >
          <template slot-scope="{ row }">
            {{ row.metrics.main_material_name || '-' }}
          </template>
        </el-table-column>

<!--        <el-table-column label="预览" width="100" align="center">-->
<!--          <template slot-scope="{ row }">-->
<!--            <el-button-->
<!--                v-if="row.preview_url"-->
<!--                type="text"-->
<!--                icon="el-icon-video-play"-->
<!--                @click="playVideo(row.preview_url)"-->
<!--            >-->
<!--              播放-->
<!--            </el-button>-->
<!--            <span v-else>-</span>-->
<!--          </template>-->
<!--        </el-table-column>-->

        <!-- 基础指标 -->
        <el-table-column
            v-if="showMetric('spend')"
            prop="metrics.spend"
            label="消耗"
            width="120"
            align="right"
        >
          <template slot-scope="{ row }">
            {{ formatMoney(row.metrics.spend) }}
          </template>
        </el-table-column>

        <el-table-column
            v-if="showMetric('impressions')"
            prop="metrics.impressions"
            label="展示量"
            width="120"
            align="right"
        >
          <template slot-scope="{ row }">
            {{ formatNumber(row.metrics.impressions) }}
          </template>
        </el-table-column>

        <!-- 注册相关指标 -->
        <el-table-column
            v-if="showMetric('onsite_unique_registration')"
            prop="metrics.onsite_unique_registration"
            label="去重注册次数"
            width="130"
            align="right"
        >
          <template slot-scope="{ row }">
            {{ formatNumber(row.metrics.onsite_unique_registration) }}
          </template>
        </el-table-column>

        <el-table-column
            v-if="showMetric('onsite_cost_per_unique_registration')"
            prop="metrics.onsite_cost_per_unique_registration"
            label="去重平均注册成本"
            width="150"
            align="right"
        >
          <template slot-scope="{ row }">
            {{ formatMoney(row.metrics.onsite_cost_per_unique_registration) }}
          </template>
        </el-table-column>

        <el-table-column
            v-if="showMetric('onsite_registration_rate')"
            prop="metrics.onsite_registration_rate"
            label="注册率(%)"
            width="120"
            align="right"
        >
          <template slot-scope="{ row }">
            {{ formatPercent(row.metrics.onsite_registration_rate) }}
          </template>
        </el-table-column>

        <el-table-column
            v-if="showMetric('onsite_total_registration')"
            prop="metrics.onsite_total_registration"
            label="注册总次数"
            width="120"
            align="right"
        >
          <template slot-scope="{ row }">
            {{ formatNumber(row.metrics.onsite_total_registration) }}
          </template>
        </el-table-column>

        <el-table-column
            v-if="showMetric('onsite_cost_per_total_registration')"
            prop="metrics.onsite_cost_per_total_registration"
            label="平均注册成本"
            width="130"
            align="right"
        >
          <template slot-scope="{ row }">
            {{ formatMoney(row.metrics.onsite_cost_per_total_registration) }}
          </template>
        </el-table-column>

        <!-- 广告展示事件指标 -->
        <el-table-column
            v-if="showMetric('onsite_unique_ad_impression')"
            prop="metrics.onsite_unique_ad_impression"
            label="去重广告展示事件数"
            width="160"
            align="right"
        >
          <template slot-scope="{ row }">
            {{ formatNumber(row.metrics.onsite_unique_ad_impression) }}
          </template>
        </el-table-column>

        <el-table-column
            v-if="showMetric('onsite_total_ad_impression')"
            prop="metrics.onsite_total_ad_impression"
            label="广告展示事件总数"
            width="150"
            align="right"
        >
          <template slot-scope="{ row }">
            {{ formatNumber(row.metrics.onsite_total_ad_impression) }}
          </template>
        </el-table-column>

        <!-- 广告收入指标 -->
        <el-table-column
            v-if="showMetric('onsite_ad_impression_ad_revenue_day0')"
            prop="metrics.onsite_ad_impression_ad_revenue_day0"
            label="第0天广告收入"
            width="140"
            align="right"
        >
          <template slot-scope="{ row }">
            {{ formatMoney(row.metrics.onsite_ad_impression_ad_revenue_day0) }}
          </template>
        </el-table-column>

        <el-table-column
            v-if="showMetric('onsite_ad_impression_ad_revenue_roas_day0')"
            prop="metrics.onsite_ad_impression_ad_revenue_roas_day0"
            label="第0天ROAS"
            width="120"
            align="right"
        >
          <template slot-scope="{ row }">
            {{ formatNumber(row.metrics.onsite_ad_impression_ad_revenue_roas_day0, 2) }}
          </template>
        </el-table-column>

        <!-- 操作列 - 仅当维度包含 smart_plus_ad_id 和 main_material_id 时显示 -->
        <el-table-column
            label="操作"
            width="180"
            align="center"
            v-if="canShowActionColumn"
        >
          <template slot-scope="{ row }">
            <el-dropdown
                @command="(command) => handleMaterialAction(command, row)"
            >
              <el-button type="primary" size="small">
                操作<i class="el-icon-arrow-down el-icon--right"></i>
              </el-button>
              <el-dropdown-menu slot="dropdown">
                <el-dropdown-item command="enable">
                  <i class="el-icon-check" style="color: #67C23A;"></i> 启用
                </el-dropdown-item>
                <el-dropdown-item command="disable">
                  <i class="el-icon-close" style="color: #F56C6C;"></i> 暂停
                </el-dropdown-item>
              </el-dropdown-menu>
            </el-dropdown>
          </template>
        </el-table-column>
      </el-table>
      <!-- 批量操作按钮 - 仅当可以显示操作列时显示 -->
      <div v-if="canShowActionColumn" style="margin-bottom: 20px;">
        <el-button
            type="success"
            size="small"
            icon="el-icon-check"
            @click="batchEnableMaterials"
            :disabled="selectedRows.length === 0"
        >
          批量启用 ({{ selectedRows.length }})
        </el-button>
        <el-button
            type="warning"
            size="small"
            icon="el-icon-close"
            @click="batchDisableMaterials"
            :disabled="selectedRows.length === 0"
        >
          批量暂停 ({{ selectedRows.length }})
        </el-button>
        <span v-if="selectedRows.length > 0" style="margin-left: 10px; color: #909399;">
    已选择 {{ selectedRows.length }} 个创意
  </span>
      </div>

      <!-- 分页 -->
      <el-pagination
          @size-change="handleSizeChange"
          @current-change="handleCurrentChange"
          :current-page="pagination.page"
          :page-sizes="[10, 20, 50, 100]"
          :page-size="pagination.pageSize"
          layout="total, sizes, prev, pager, next, jumper"
          :total="pagination.total"    style="margin-top: 20px; text-align: right;"
      />

    </el-card>

    <!-- 视频播放对话框 -->
    <el-dialog title="视频预览" :visible.sync="videoDialogVisible" width="800px">
      <video
          v-if="currentVideoUrl"
          :src="currentVideoUrl"
          controls
          autoplay
          style="width: 100%; max-height: 500px;"
      >
        您的浏览器不支持视频播放
      </video>
    </el-dialog>
  </div>
</template>

<script>
import { getSmartPlusMaterialReport,
  enableMaterials,
  disableMaterials  } from '@/api/smartPlus'
import { advertiserApi } from '@/api/advertiserApi'

export default {
  name: 'SmartPlusMaterialReport',
  data() {
    return {
      loading: false,
      queryType: 'date',

      // 查询表单
      queryForm: {
        advertiserId: '',
        dimensions: ['smart_plus_ad_id', 'main_material_id'],
        startDate: '',
        endDate: '',
        sortField: 'spend',
        sortType: 'DESC',
        page: 1,
        pageSize: 10
      },

      // 选中的指标
      selectedMetrics: ['spend', 'impressions', 'ad_material_id','onsite_ad_impression_ad_revenue_roas_day0'],
      selectAllMetrics: false,

      // 表格数据
      tableData: [],

      // 选中的行
      selectedRows: [],

      // 分页
      pagination: {
        page: 1,
        pageSize: 10,
        total: 0
      },

      // 视频播放
      videoDialogVisible: false,
      currentVideoUrl: '',

      // 广告主列表
      advertiserList: [],

      // 维度选项
      dimensionOptions: [
        { value: 'advertiser_id', label: '广告主ID' },
        { value: 'campaign_id', label: '推广系列ID' },
        { value: 'adgroup_id', label: '广告组ID' },
        { value: 'smart_plus_ad_id', label: '广告ID' },
        { value: 'main_material_id', label: '主要素材ID' }
      ],

      // 指标选项
      metricOptions: [
        { value: 'spend', label: '消耗' },
        { value: 'impressions', label: '展示量' },
        { value: 'onsite_unique_registration', label: '去重注册次数' },
        { value: 'onsite_cost_per_unique_registration', label: '去重平均注册成本' },
        { value: 'onsite_registration_rate', label: '注册率(%)' },
        { value: 'onsite_total_registration', label: '注册总次数' },
        { value: 'onsite_cost_per_total_registration', label: '平均注册成本' },
        { value: 'onsite_unique_ad_impression', label: '去重广告展示事件数' },
        { value: 'onsite_cost_per_unique_ad_impression', label: '去重广告展示事件平均成本' },
        { value: 'onsite_ad_impression_rate', label: '广告展示事件率' },
        { value: 'onsite_total_ad_impression', label: '广告展示事件总数' },
        { value: 'onsite_cost_per_total_ad_impression', label: '广告展示事件平均成本' },
        { value: 'onsite_total_ad_impression_value', label: '广告展示总价值' },
        { value: 'onsite_value_per_ad_impression_value', label: '广告展示事件平均价值' },
        { value: 'onsite_ad_impression_ad_revenue_day0', label: '第0天广告收入' },
        { value: 'onsite_ad_impression_ad_revenue_day6', label: '第6天广告收入' },
        { value: 'onsite_ad_impression_ad_revenue_day13', label: '第13天广告收入' },
        { value: 'onsite_ad_impression_ad_revenue_calendar_day0', label: '第0个日历日广告收入' },
        { value: 'onsite_ad_impression_ad_revenue_calendar_day6', label: '第6个日历日广告收入' },
        { value: 'onsite_ad_impression_ad_revenue_calendar_day13', label: '第13个日历日广告收入' },
        { value: 'onsite_ad_impression_ad_revenue_roas_day0', label: '第0天ROAS' },
        { value: 'onsite_ad_impression_ad_revenue_roas_day6', label: '第6天ROAS' },
        { value: 'onsite_ad_impression_ad_revenue_roas_day13', label: '第13天ROAS' },
        { value: 'onsite_ad_impression_ad_revenue_roas_calendar_day0', label: '第0个日历日ROAS' },
        { value: 'onsite_ad_impression_ad_revenue_roas_calendar_day6', label: '第6个日历日ROAS' },
        { value: 'onsite_ad_impression_ad_revenue_roas_calendar_day13', label: '第13个日历日ROAS' }
      ],

      // 表单验证规则
      rules: {
        advertiserId: [
          { required: true, message: '请输入广告主ID', trigger: 'blur' }
        ],
        dimensions: [
          { required: true, message: '请选择维度分组', trigger: 'change' },
          { 
            validator: (rule, value, callback) => {
              if (!value || value.length === 0) {
                callback(new Error('请至少选择2个维度'))
              } else if (value.length !== 2) {
                callback(new Error('必须选择且只能选择2个维度'))
              } else {
                callback()
              }
            }, 
            trigger: 'change' 
          }
        ],
        startDate: [
          { required: true, message: '请选择开始日期', trigger: 'change' }
        ],
        endDate: [
          { required: true, message: '请选择结束日期', trigger: 'change' }
        ]
      }
    }
  },

  computed: {

    // 判断是否可以显示操作列（维度包含 smart_plus_ad_id 和 main_material_id）
    canShowActionColumn() {
      return this.queryForm.dimensions.includes('smart_plus_ad_id') &&
          this.queryForm.dimensions.includes('main_material_id')
    },

    // 排序字段选项（基于已选择的指标）
    sortFieldOptions() {
      return this.metricOptions.filter(metric =>
          this.selectedMetrics.includes(metric.value)
      )
    },

    // 总消耗
    totalSpend() {
      return this.tableData.reduce((sum, item) => {
        return sum + (parseFloat(item.metrics?.spend) || 0)
      }, 0)
    },

    // 总展示量
    totalImpressions() {
      return this.tableData.reduce((sum, item) => {
        return sum + (parseInt(item.metrics?.impressions) || 0)
      }, 0)
    },

    // 总注册次数
    totalRegistrations() {
      return this.tableData.reduce((sum, item) => {
        return sum + (parseInt(item.metrics?.onsite_unique_registration) || 0)
      }, 0)
    },

  },

  created() {
    this.initDefaultDates()
    this.fetchAdvertisers()
  },

  methods: {

    // 处理表格选择变化
    handleSelectionChange(selection) {
      this.selectedRows = selection
    },

    // 处理创意操作（启用/暂停）
    handleMaterialAction(command, row) {
      const adMaterialId = row.metrics?.ad_material_id
      const adId = row.dimensions?.smart_plus_ad_id

      if (!adMaterialId || !adId) {
        this.$message.warning('缺少必要的素材ID或广告ID')
        return
      }

      // ad_material_id 可能是数组或字符串，统一转为数组
      const materialIds = Array.isArray(adMaterialId) ? adMaterialId : [adMaterialId]

      const actionText = command === 'enable' ? '启用' : '暂停'

      this.$confirm(`确认要${actionText}该创意吗？`, '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(() => {
        this.executeMaterialAction(command, adId, materialIds)
      }).catch(() => {
        this.$message.info('已取消操作')
      })
    },

    // 执行创意操作
    executeMaterialAction(action, adId, materialIds) {
      const actionText = action === 'enable' ? '启用' : '暂停'
      const apiFunc = action === 'enable' ? enableMaterials : disableMaterials

      const params = {
        advertiserId: this.queryForm.advertiserId,
        smartPlusAdId: adId,
        adMaterialIds: materialIds,
        operationStatus: action === 'enable' ? 'ENABLE' : 'DISABLE'
      }

      this.loading = true
      apiFunc(params).then(response => {
        if (response.code === 200 && response.data.code === 0) {
          this.$message.success(`${actionText}成功`)
          // 刷新数据
          this.handleQuery()
        } else {
          this.$message.error(response.data.message || `${actionText}失败`)
        }
      }).catch(error => {
        console.error(`${actionText}失败:`, error)
        this.$message.error(`${actionText}失败: ` + (error.response?.data?.message || error.message))
      }).finally(() => {
        this.loading = false
      })
    },

    // 批量启用选中的创意
    batchEnableMaterials() {
      if (this.selectedRows.length === 0) {
        this.$message.warning('请先选择要操作的创意')
        return
      }

      this.$confirm(`确认要启用选中的 ${this.selectedRows.length} 个创意吗？`, '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(() => {
        // 按广告ID分组
        const groupedByAd = {}
        this.selectedRows.forEach(row => {
          const adId = row.dimensions?.smart_plus_ad_id
          const adMaterialId = row.metrics?.ad_material_id
          if (adId && adMaterialId) {
            if (!groupedByAd[adId]) {
              groupedByAd[adId] = []
            }
            // ad_material_id 可能是数组，需要展开
            const ids = Array.isArray(adMaterialId) ? adMaterialId : [adMaterialId]
            groupedByAd[adId].push(...ids)
          }
        })

        // 对每个广告执行批量启用
        const promises = Object.entries(groupedByAd).map(([adId, materialIds]) => {
          return enableMaterials({
            advertiserId: this.queryForm.advertiserId,
            smartPlusAdId: adId,
            adMaterialIds: materialIds,
            operationStatus: 'ENABLE'
          })
        })

        this.loading = true
        Promise.all(promises).then(results => {
          const successCount = results.filter(r => r.code === 200 && r.data.code === 0).length
          this.$message.success(`批量启用完成，成功 ${successCount} 个广告`)
          this.handleQuery()
        }).catch(error => {
          this.$message.error('批量启用失败: ' + error.message)
        }).finally(() => {
          this.loading = false
        })
      }).catch(() => {})
    },

    // 批量暂停选中的创意
    batchDisableMaterials() {
      if (this.selectedRows.length === 0) {
        this.$message.warning('请先选择要操作的创意')
        return
      }

      this.$confirm(`确认要暂停选中的 ${this.selectedRows.length} 个创意吗？`, '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(() => {
        // 按广告ID分组
        const groupedByAd = {}
        this.selectedRows.forEach(row => {
          const adId = row.dimensions?.smart_plus_ad_id
          const adMaterialId = row.metrics?.ad_material_id
          if (adId && adMaterialId) {
            if (!groupedByAd[adId]) {
              groupedByAd[adId] = []
            }
            // ad_material_id 可能是数组，需要展开
            const ids = Array.isArray(adMaterialId) ? adMaterialId : [adMaterialId]
            groupedByAd[adId].push(...ids)
          }
        })

        // 对每个广告执行批量暂停
        const promises = Object.entries(groupedByAd).map(([adId, materialIds]) => {
          return disableMaterials({
            advertiserId: this.queryForm.advertiserId,
            smartPlusAdId: adId,
            adMaterialIds: materialIds,
            operationStatus: 'DISABLE'
          })
        })

        this.loading = true
        Promise.all(promises).then(results => {
          const successCount = results.filter(r => r.code === 200 && r.data.code === 0).length
          this.$message.success(`批量暂停完成，成功 ${successCount} 个广告`)
          this.handleQuery()
        }).catch(error => {
          this.$message.error('批量暂停失败: ' + error.message)
        }).finally(() => {
          this.loading = false
        })
      }).catch(() => {})
    },
    // 初始化默认日期（昨天到今天）
    initDefaultDates() {
      const today = new Date()
      const yesterday = new Date(today)
      yesterday.setDate(yesterday.getDate() - 1)

      // 格式化为 yyyy-MM-dd
      const formatDate = (date) => {
        const year = date.getFullYear()
        const month = String(date.getMonth() + 1).padStart(2, '0')
        const day = String(date.getDate()).padStart(2, '0')
        return `${year}-${month}-${day}`
      }

      this.queryForm.startDate = formatDate(yesterday)
      this.queryForm.endDate = formatDate(today)
    },

    // 获取广告主列表
    async fetchAdvertisers() {
      try {
        const res = await advertiserApi.getList({
          page: 1,
          pageSize: 100
        })
        if (res.code === 0) {
          this.advertiserList = res.data.list || []
          // 如果有广告主且当前未选择，自动选择第一个
          if (this.advertiserList.length > 0 && !this.queryForm.advertiserId) {
            this.queryForm.advertiserId = this.advertiserList[0].advertiser_id
          }
        }
      } catch (error) {
        console.error('获取广告主列表失败', error)
        this.$message.error('获取广告主列表失败')
      }
    },

    // 查询数据
    handleQuery() {
      this.$refs.queryForm.validate((valid) => {
        if (valid) {
          this.loading = true

          const params = {
            advertiserId: this.queryForm.advertiserId,
            dimensions: this.queryForm.dimensions,
            metrics: this.selectedMetrics.length > 0 ? this.selectedMetrics : ['spend', 'impressions'],
            queryLifetime: this.queryType === 'lifetime',
            sortField: this.queryForm.sortField,
            sortType: this.queryForm.sortType,
            page: this.pagination.page,
            pageSize: this.pagination.pageSize
          }

          if (this.queryType === 'date') {
            params.startDate = this.queryForm.startDate
            params.endDate = this.queryForm.endDate
          }

          getSmartPlusMaterialReport(params)
              .then(response => {
                if (response.code === 200 && response.data.code === 0) {
                  const result = response.data.data

                  this.tableData = result.list || []

                  if (result.page_info) {
                    this.pagination.total = result.page_info.total_number || 0
                    this.pagination.page = result.page_info.page || 1
                    this.pagination.pageSize = result.page_info.page_size || 10
                  }

                  this.$message.success('查询成功')
                } else {
                  this.$message.error(response.data.message || '查询失败')
                }
              })
              .catch(error => {
                console.error('查询失败:', error)
                this.$message.error('查询失败: ' + (error.response?.data?.message || error.message))
              })
              .finally(() => {
                this.loading = false
              })
        }
      })
    },

    // 重置表单
    resetForm() {
      this.$refs.queryForm.resetFields()
      this.selectedMetrics = ['spend', 'impressions']
      this.selectAllMetrics = false
      this.queryType = 'date'
      this.tableData = []
      this.pagination = {
        page: 1,
        pageSize: 10,
        total: 0
      }
    },

    // 全选/取消全选指标
    handleSelectAllMetrics(val) {
      if (val) {
        this.selectedMetrics = this.metricOptions.map(m => m.value)
      } else {
        this.selectedMetrics = ['spend', 'impressions']
      }
    },

    // 导出数据
    exportData() {
      const headers = ['素材ID', '素材类型', '素材名称', '消耗', '展示量', '去重注册次数']
      const rows = this.tableData.map(item => [
        item.dimensions?.main_material_id || '',
        this.formatMaterialType(item.dimensions?.main_material_type),
        item.metrics?.main_material_name || '',
        item.metrics?.spend || '0',
        item.metrics?.impressions || '0',
        item.metrics?.onsite_unique_registration || '0'
      ])

      let csvContent = '\ufeff' + headers.join(',') + '\n'
      rows.forEach(row => {
        csvContent += row.join(',') + '\n'
      })

      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
      const link = document.createElement('a')
      link.href = URL.createObjectURL(blob)
      link.download = `smart_plus_report_${new Date().getTime()}.csv`
      link.click()

      this.$message.success('导出成功')
    },

    // 播放视频
    playVideo(url) {
      this.currentVideoUrl = url
      this.videoDialogVisible = true
    },

    // 分页大小改变
    handleSizeChange(val) {
      this.pagination.pageSize = val
      this.pagination.page = 1
      this.handleQuery()
    },

    // 页码改变
    handleCurrentChange(val) {
      this.pagination.page = val
      this.handleQuery()
    },

    // 格式化金额
    formatMoney(value) {
      if (value === null || value === undefined || value === '') {
        return '-'
      }
      const num = parseFloat(value)
      if (isNaN(num)) {
        return '-'
      }
      return num.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',')
    },

    // 格式化数字
    formatNumber(value, decimals = 0) {
      if (value === null || value === undefined || value === '') {
        return '-'
      }
      const num = parseFloat(value)
      if (isNaN(num)) {
        return '-'
      }
      return num.toFixed(decimals).replace(/\B(?=(\d{3})+(?!\d))/g, ',')
    },

    // 格式化百分比
    formatPercent(value) {
      if (value === null || value === undefined || value === '') {
        return '-'
      }
      const num = parseFloat(value)
      if (isNaN(num)) {
        return '-'
      }
      return num.toFixed(2) + '%'
    },

    // 格式化素材类型
    formatMaterialType(type) {
      const typeMap = {
        'VIDEO_NON_SPARK_ADS': '视频(非Spark)',
        'VIDEO_SPARK_ADS': '视频(Spark)',
        'CAROUSEL_NON_SPARK_ADS': '轮播(非Spark)',
        'CAROUSEL_SPARK_ADS': '轮播(Spark)',
        'SINGLE_IMAGE_NON_SPARK_ADS': '单图(非Spark)',
        'CATALOG_VIDEO': '商品库视频',
        'CATALOG_VIDEO_TEMPLATE': '商品库视频模板',
        'CATALOG_CAROUSEL': '商品库轮播',
        'CATALOG_MULTI_SHOW': '商品库多影片'
      }
      return typeMap[type] || type
    },

    // 判断是否显示某列
    showColumn(columnName) {
      return this.queryForm.dimensions.includes(columnName)
    },

    // 判断是否显示某指标
    showMetric(metricName) {
      return this.selectedMetrics.includes(metricName)
    }
  }
}
</script>

<style scoped>
.smart-plus-material-report {
  padding: 16px;
  min-height: 100%;
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.page-header h2 {
  margin: 0;
  font-size: 20px;
  color: #303133;
}

.filter-card,
.metrics-card {
  margin-bottom: 20px;
}

.table-card {
  margin-bottom: 20px;
}

.stats-row {
  margin-bottom: 20px;
}

.stat-card {
  text-align: center;
}

.stat-item {
  padding: 10px 0;
}

.stat-label {
  font-size: 14px;
  color: #909399;
  margin-bottom: 8px;
}

.stat-value {
  font-size: 24px;
  font-weight: bold;
  color: #303133;
}
</style>
